import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { X } from "lucide-react";
import { EASE } from "./Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const LOOKUP_BY_SLUG = {
  "sea-safari": "sea_safari_tour",
  "boat-trips": "rib_boat_tour",
  "kayaking-sup": "kayak_sup_tour",
  "sauna-experience": "beach_sauna_experience",
};

export const MAX_GUESTS = {
  "sea-safari": 16,
  "boat-trips": 16,
  "kayaking-sup": 25,
  "sauna-experience": 15,
};

export const MIN_GUESTS = {
  "sea-safari": 6,
  "boat-trips": 6,
  "kayaking-sup": 6,
  "sauna-experience": 6,
};

export default function BookingModal({ open, onClose, exp, pricePerPerson }) {
  const [form, setForm] = useState({ date: "", guests: 2, name: "", email: "", phone: "", notes: "" });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (exp) {
      const min = MIN_GUESTS[exp.slug] || 1;
      setForm((f) => ({ ...f, guests: Math.max(Number(f.guests) || 1, min) }));
    }
  }, [exp]);

  if (!exp) return null;
  const maxGuests = MAX_GUESTS[exp.slug] || 16;
  const minGuests = MIN_GUESTS[exp.slug] || 1;
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const total = pricePerPerson * form.guests;

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    try {
      const res = await fetch(`${API}/payments/checkout`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          lookup_key: LOOKUP_BY_SLUG[exp.slug],
          quantity: Number(form.guests),
          origin_url: window.location.origin,
          name: form.name,
          email: form.email,
          phone: form.phone,
          preferred_date: form.date,
          notes: form.notes,
        }),
      });
      const data = await res.json();
      if (data.checkout_url) {
        window.location.href = data.checkout_url;
        return;
      }
      setError(typeof data.detail === "string" ? data.detail : "Could not start checkout — please try again.");
    } catch (err) {
      setError("Could not start checkout — please try again or call +353 86 251 8252.");
    } finally {
      setSubmitting(false);
    }
  };

  const inputCls =
    "w-full border-b border-white/20 bg-transparent px-0 py-2.5 text-sm text-ivory placeholder:text-mist/50 transition-colors duration-300 focus:border-gold focus:outline-none";
  const labelCls = "text-[11px] font-medium uppercase tracking-[0.2em] text-mist";

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.3 }}
          className="fixed inset-0 z-[85] flex items-end justify-center bg-navy-950/80 backdrop-blur-sm sm:items-center"
          onClick={onClose}
        >
          <motion.div
            initial={{ opacity: 0, y: 40, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 40, scale: 0.98 }}
            transition={{ duration: 0.4, ease: EASE }}
            onClick={(e) => e.stopPropagation()}
            data-testid="booking-modal"
            className="max-h-[92vh] w-full max-w-lg overflow-y-auto border border-gold/30 bg-navy-900 p-7 sm:p-9"
          >
            <div className="flex items-start justify-between gap-6">
              <div>
                <p className="text-[10px] font-semibold uppercase tracking-[0.3em] text-gold">Book Now</p>
                <h3 className="mt-2 font-serif text-3xl leading-tight text-ivory">{exp.title}</h3>
              </div>
              <button
                onClick={onClose}
                data-testid="booking-modal-close"
                aria-label="Close booking"
                className="border border-white/15 p-2 text-ivory/70 transition-colors hover:border-gold hover:text-gold"
              >
                <X className="h-4 w-4" />
              </button>
            </div>

            <div className="mt-5 flex items-center justify-between border border-gold/25 bg-navy-800/60 px-5 py-4">
              <span className="text-[12px] uppercase tracking-[0.18em] text-mist">
                &euro;{pricePerPerson} per person
              </span>
              <span className="font-serif text-2xl text-gold" data-testid="booking-total">&euro;{total}</span>
            </div>

            <form onSubmit={submit} data-testid="booking-form" className="mt-7 space-y-6">
              <div className="grid gap-6 sm:grid-cols-2">
                <div>
                  <label htmlFor="bk-date" className={labelCls}>Preferred Date *</label>
                  <input id="bk-date" type="date" required value={form.date} onChange={set("date")} data-testid="booking-date-input" className={inputCls} />
                </div>
                <div>
                  <label htmlFor="bk-guests" className={labelCls}>
                    {minGuests > 1 ? `Guests (min ${minGuests} — max ${maxGuests})` : `Guests (max ${maxGuests})`}
                  </label>
                  <select id="bk-guests" value={form.guests} onChange={set("guests")} data-testid="booking-guests-select" className={`${inputCls} appearance-none [&>option]:bg-navy-900`}>
                    {Array.from({ length: maxGuests - minGuests + 1 }, (_, i) => i + minGuests).map((n) => (
                      <option key={n} value={n}>{n}{n === minGuests ? " (minimum)" : ""}</option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="grid gap-6 sm:grid-cols-2">
                <div>
                  <label htmlFor="bk-name" className={labelCls}>Name *</label>
                  <input id="bk-name" required value={form.name} onChange={set("name")} data-testid="booking-name-input" placeholder="Your name" className={inputCls} />
                </div>
                <div>
                  <label htmlFor="bk-email" className={labelCls}>Email *</label>
                  <input id="bk-email" type="email" required value={form.email} onChange={set("email")} data-testid="booking-email-input" placeholder="you@example.com" className={inputCls} />
                </div>
              </div>
              <div>
                <label htmlFor="bk-phone" className={labelCls}>Phone</label>
                <input id="bk-phone" type="tel" value={form.phone} onChange={set("phone")} data-testid="booking-phone-input" placeholder="+353 ..." className={inputCls} />
              </div>
              <div>
                <label htmlFor="bk-notes" className={labelCls}>Anything We Should Know?</label>
                <textarea id="bk-notes" rows={2} value={form.notes} onChange={set("notes")} data-testid="booking-notes-input" placeholder="Ages, occasions, requests..." className={`${inputCls} resize-none`} />
              </div>
              {error && <p className="text-sm text-red-300" data-testid="booking-error">{error}</p>}
              <button
                type="submit"
                disabled={submitting}
                data-testid="booking-submit-button"
                className="w-full bg-gold py-4 text-[12px] font-semibold uppercase tracking-[0.24em] text-navy-800 transition-colors duration-300 hover:bg-gold-light disabled:cursor-wait disabled:opacity-70"
              >
                {submitting ? "Opening Secure Checkout..." : `Continue to Payment — \u20AC${total}`}
              </button>
              <p className="text-center text-[11px] tracking-wide text-mist">
                Secure payment by card through Stripe. You'll receive a confirmation by email.
              </p>
            </form>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
